import { useCallback, useEffect, useRef, useState } from 'react';
import type { Geometry } from 'geojson';
import request from 'lib/request';

/** Enough of a well row to list one; the query returns far more per well. */
export interface IntersectedWell {
  id: number;
  uwi: string;
  name: string;
  currentOperator: string | null;
  status: string | null;
  href: string;
}

interface WellsQueryResponse {
  results: IntersectedWell[];
  totalCount: number;
}

export interface IntersectedWells {
  wells: IntersectedWell[];
  /** Total matching the shape, which can exceed the page of rows held here. */
  totalCount: number | null;
  loading: boolean;
  error: string | null;
  /** Runs the match against the shape supplied at call time. */
  find: () => void;
  /** Clears results — used when the shape changes underneath them. */
  reset: () => void;
}

/** One page is plenty to show what a boundary caught; the count carries the rest. */
const PAGE_SIZE = 100;

const EMPTY = {
  wells: [] as IntersectedWell[],
  totalCount: null as number | null,
  loading: false,
  error: null as string | null,
};

/**
 * Wells whose geometry falls inside the given shape, via the shared wells query.
 * The count is what the panel shows before saving a Unit.
 *
 * @param {Geometry | null} geometry - The shape to match against.
 * @return {IntersectedWells} The results plus the trigger.
 */
export const useIntersectedWells = (
  geometry: Geometry | null
): IntersectedWells => {
  const [state, setState] = useState(EMPTY);
  const controllerRef = useRef<AbortController | null>(null);

  // Results describe one shape. Once it changes they are stale, so drop them
  // rather than leave a count next to a boundary it wasn't measured from.
  useEffect(() => {
    setState(EMPTY);
    return () => controllerRef.current?.abort();
  }, [geometry]);

  const find = useCallback(() => {
    if (!geometry) {
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setState({ ...EMPTY, loading: true });

    request
      .post<WellsQueryResponse, unknown>(
        '/queries/wells',
        // Wrapped in `query`, as the other query endpoints take it.
        {
          query: {
            polygon_geojson: geometry,
            page: 1,
            pageSize: PAGE_SIZE,
            use_elastic: true,
          },
        },
        controller
      )
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        if (response.type !== 'success') {
          setState({ ...EMPTY, error: 'Could not match wells to this shape.' });
          return;
        }
        setState({
          wells: response.data.results ?? [],
          totalCount: response.data.totalCount ?? 0,
          loading: false,
          error: null,
        });
      });
  }, [geometry]);

  const reset = useCallback(() => setState(EMPTY), []);

  return { ...state, find, reset };
};
